import DOMPurify from 'dompurify';

/**
 * Text conversion utilities for rich text editing
 *
 * Architecture:
 * - Rich text editor displays HTML to users (sanitized with DOMPurify for security)
 * - Data is stored as formatted strings in the backend (using htmlToString/stringToHtml)
 * - Internal logic works with string format, not HTML
 */

/**
 * Converts HTML content to a formatted string for backend storage
 * @param html - HTML content from React Quill
 * @returns Formatted string for backend storage
 */
export const htmlToString = (html: string): string => {
  if (!html || html.trim() === '') return '';

  // Remove any leading/trailing whitespace and normalize
  let content = html.trim();

  // Handle basic formatting - process links first before other tags are removed
  content = content
    // Links: <a href="url">text</a> -> [text](url) (preserve links as markdown-style)
    // Process links first to preserve them before other HTML tags are removed
    .replace(/<a\s+[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi, (match, href, text) => {
      // Check if target="_blank" exists in the original match
      const hasTargetBlank = /target=["']_blank["']/i.test(match);
      // Clean up the link text by removing nested HTML tags (they'll be processed separately)
      const cleanText = text.replace(/<[^>]*>/g, '');
      // If target is _blank, add indicator
      const linkText = hasTargetBlank ? `${cleanText} ↗` : cleanText;
      return `[${linkText}](${href})`;
    })
    // Bold: <strong>text</strong> or <b>text</b> -> **text**
    .replace(/<(strong|b)>(.*?)<\/(strong|b)>/g, '**$2**')
    // Italic: <em>text</em> or <i>text</i> -> *text*
    .replace(/<(em|i)>(.*?)<\/(em|i)>/g, '*$2*')
    // Strikethrough: <s>text</s> or <strike>text</strike> -> ~~text~~
    .replace(/<(s|strike)>(.*?)<\/(s|strike)>/g, '~~$2~~')
    // Line breaks: <br> or <br/> -> \n
    .replace(/<br\s*\/?>/g, '\n')
    // Paragraphs: <p>text</p> -> text\n
    .replace(/<p>(.*?)<\/p>/g, '$1\n')
    // Lists: <ul><li>item</li></ul> -> • item\n
    .replace(/<ul>(.*?)<\/ul>/g, (_match, content) => {
      return content.replace(/<li>(.*?)<\/li>/g, '• $1\n');
    })
    // Ordered lists: <ol><li>item</li></ol> -> 1. item\n
    .replace(/<ol>(.*?)<\/ol>/g, (_match, content) => {
      let counter = 1;
      return content.replace(
        /<li>(.*?)<\/li>/g,
        (_liMatch: string, liContent: string) =>
          `${counter++}. ${liContent}\n`
      );
    })
    // Quotes: <blockquote>text</blockquote> -> > text\n
    .replace(/<blockquote>(.*?)<\/blockquote>/g, '> $1\n')
    // Headers: <h1>text</h1> -> # text\n
    .replace(/<h1>(.*?)<\/h1>/g, '# $1\n')
    .replace(/<h2>(.*?)<\/h2>/g, '## $1\n')
    .replace(/<h3>(.*?)<\/h3>/g, '### $1\n')
    // Remove any remaining HTML tags
    .replace(/<[^>]*>/g, '')
    // Decode HTML entities
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    // Clean up multiple newlines
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    // Trim and normalize
    .trim();

  return content;
};

/**
 * Converts formatted string back to HTML for display
 * @param text - Formatted string from backend
 * @returns HTML content for React Quill or display
 */
export const stringToHtml = (text: string): string => {
  if (!text || text.trim() === '') return '';

  let content = text.trim();

  // Step 1: Convert formatting to HTML
  content = content
    // Links: [text](url) or [text ↗](url) -> <a href="url" target="_blank">text</a>
    .replace(/\[([^\]]+?)(\s+↗)?\]\(([^)]+)\)/g, (_match, text, hasArrow, url) => {
      const linkText = hasArrow ? text.trim() : text;
      const target = hasArrow ? ' target="_blank" rel="noopener noreferrer"' : '';
      return `<a href="${url}"${target}>${linkText}</a>`;
    })
    // Bold: **text** -> <strong>text</strong>
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic: *text* -> <em>text</em>
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Strikethrough: ~~text~~ -> <s>text</s>
    .replace(/~~(.*?)~~/g, '<s>$1</s>')
    // Quotes: > text -> <blockquote>text</blockquote>
    .replace(/^>\s+(.*?)$/gm, '<blockquote>$1</blockquote>')
    // Headers: # text -> <h1>text</h1>
    .replace(/^#\s+(.*?)$/gm, '<h1>$1</h1>')
    .replace(/^##\s+(.*?)$/gm, '<h2>$1</h2>')
    .replace(/^###\s+(.*?)$/gm, '<h3>$1</h3>');

  // Step 2: Handle lists by grouping consecutive items
  // First, split content into lines to process lists properly
  const lines = content.split('\n');
  const processedLines: string[] = [];
  let currentList: string[] = [];
  let isInList = false;
  let isOrderedList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isNumberedItem = /^\d+\.\s*/.test(line);
    const isBulletItem = /^•\s*/.test(line);

    if (isNumberedItem || isBulletItem) {
      // Start or continue a list
      if (!isInList) {
        isInList = true;
        isOrderedList = isNumberedItem;
        currentList = [];
      }

      // Add the list item (remove the marker, but preserve empty content)
      const itemContent = line.replace(/^(\d+\.|•)\s*/, '');
      currentList.push(`<li>${itemContent}</li>`);
    } else {
      // End of list or regular line
      if (isInList && currentList.length > 0) {
        // Close the current list
        const listTag = isOrderedList ? 'ol' : 'ul';
        processedLines.push(
          `<${listTag}>${currentList.join('')}</${listTag}>`
        );
        currentList = [];
        isInList = false;
      }

      // Add the regular line (preserve empty lines for proper spacing)
      processedLines.push(line);
    }
  }

  // Handle case where list is at the end
  if (isInList && currentList.length > 0) {
    const listTag = isOrderedList ? 'ol' : 'ul';
    processedLines.push(
      `<${listTag}>${currentList.join('')}</${listTag}>`
    );
  }

  // Rejoin the content
  content = processedLines.join('\n');

  // Step 3: Convert line breaks to <br> tags
  content = content.replace(/\n/g, '<br>');

  // Step 4: Clean up empty tags and normalize
  content = content
    // Clean up empty tags (but preserve empty list items)
    .replace(/<(strong|em|s|blockquote|h[1-6])><\/\1>/g, '')
    // Clean up multiple <br> tags
    .replace(/(<br>)+/g, '<br>')
    // Remove trailing <br> at the end
    .replace(/<br>$/g, '');

  return content;
};

/**
 * Safely renders HTML content with comprehensive sanitization using DOMPurify
 * This provides production-ready XSS protection for the rich text editor display
 *
 * Note: This is used only for displaying HTML to users in the rich text editor.
 * The actual data storage uses string format via htmlToString/stringToHtml functions.
 *
 * @param html - HTML content to render in the rich text editor
 * @returns Sanitized HTML string safe for rendering to users
 */
export const sanitizeHtml = (html: string): string => {
  if (!html || typeof html !== 'string') return '';

  // Handle whitespace-only input
  if (html.trim() === '') return '';

  // Pre-process to remove links with dangerous protocols before sanitization
  // This ensures javascript: and other dangerous protocols are completely removed
  let preprocessedHtml = html;
  preprocessedHtml = preprocessedHtml.replace(
    /<a\s+[^>]*href=["'](javascript|data|vbscript):[^"']*["'][^>]*>.*?<\/a>/gi,
    (match) => {
      // Extract text content from the link
      const textMatch = match.match(/>(.*?)<\/a>/);
      return textMatch ? textMatch[1] : '';
    }
  );

  // Use DOMPurify for comprehensive XSS protection
  // Configure allowed tags and attributes for our use case
  const result = DOMPurify.sanitize(preprocessedHtml, {
    // Only allow safe formatting tags that our app uses
    ALLOWED_TAGS: [
      'strong',
      'em',
      's',
      'br',
      'p',
      'ul',
      'ol',
      'li',
      'blockquote',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'a',
    ],
    // Only allow safe attributes
    ALLOWED_ATTR: ['class', 'id', 'style', 'href', 'target', 'rel'],
    // Keep text content even if dangerous tags are removed
    KEEP_CONTENT: true,
    // Don't fix malformed HTML structure
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    SANITIZE_DOM: false,
    WHOLE_DOCUMENT: false,
    // Remove any remaining dangerous content
    FORBID_TAGS: [
      'script',
      'iframe',
      'object',
      'embed',
      'form',
      'input',
      'textarea',
      'select',
      'button',
      'link',
      'meta',
      'style',
      'title',
      'head',
      'body',
      'html',
    ],
    // Remove dangerous attributes
    FORBID_ATTR: [
      'onerror',
      'onload',
      'onclick',
      'onmouseover',
      'onfocus',
      'onblur',
      'onchange',
      'onsubmit',
      'onreset',
      'onselect',
      'onunload',
      'onresize',
      'onabort',
      'onbeforeunload',
      'onerror',
      'onhashchange',
      'onmessage',
      'onoffline',
      'ononline',
      'onpagehide',
      'onpageshow',
      'onpopstate',
      'onstorage',
      'oncontextmenu',
      'onkeydown',
      'onkeypress',
      'onkeyup',
      'onmousedown',
      'onmousemove',
      'onmouseout',
      'onmouseup',
      'onwheel',
      'oncopy',
      'oncut',
      'onpaste',
      'onselectstart',
      'onselectionchange',
    ],
    // Remove protocols that could be dangerous
    ALLOW_DATA_ATTR: false,
    // Remove any remaining dangerous protocols
    ALLOWED_URI_REGEXP:
      /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  });

  // If input was safe and DOMPurify auto-fixed malformed HTML, prefer the original
  // This handles cases like <strong>Bold<em>Italic</strong> where we don't want auto-fixing
  // Only bypass for safe formatting tags that are in our allowed list
  const onlyAllowedTags =
    /^[^<]*(<\/?(?:strong|em|s|br|p|ul|ol|li|blockquote|h[1-6]|a)[^>]*>|[^<]+)*[^<]*$/i.test(
      html
    );
  if (
    html.length > 0 &&
    result !== html &&
    onlyAllowedTags &&
    !/<script|<iframe|javascript:|on\w+=/i.test(html)
  ) {
    return html;
  }

  return result;
};

/**
 * Gets the plain text length of HTML content by stripping all HTML tags
 * Used for character count validation against backend limits
 * @param html - HTML content from React Quill
 * @returns Number of visible characters (excluding HTML tags)
 */
export const getPlainTextLength = (html: string): number => {
  if (!html || typeof html !== 'string') return 0;
  if (html.trim() === '') return 0;

  // Create a temporary DOM element to safely strip HTML tags
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Get the plain text content
  const plainText = tempDiv.textContent || tempDiv.innerText || '';

  // Return the length of the plain text
  return plainText.length;
};

/**
 * Gets the visible text content from HTML, properly handling entities and whitespace
 * This provides more accurate character counting for user-visible content
 * @param html - HTML content from React Quill
 * @returns Visible text string with normalized whitespace and entities
 */
export const getVisibleText = (html: string): string => {
  if (!html || typeof html !== 'string') return '';
  if (html.trim() === '') return '';

  // Create a temporary DOM element to safely process HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Get the text content (this automatically decodes HTML entities)
  let visibleText = tempDiv.textContent || tempDiv.innerText || '';

  // Normalize whitespace and entities
  visibleText = visibleText
    // Replace non-breaking spaces with regular spaces
    .replace(/\u00A0/g, ' ')
    // Normalize CRLF to \n
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Collapse multiple spaces only if they were likely from HTML entities
    // (don't over-trim user-typed spaces)
    .replace(/[ \t]+/g, ' ')
    // Trim only leading/trailing whitespace, not internal
    .trim();

  return visibleText;
};
